import {EmptyArray, TEMPLATE_DILEMMA, TEMPLATE_PICTURESLIDES} from '~/utils/constants';
import {createSelector} from 'reselect';
import {getConfigObject} from '~/stores/editor/selectors/config.selectors';
import {getThemeFromURLParams} from '~/utils/default-theme';
import {
  DEFAULT_BG_COLOR,
  DEFAULT_BG_INPUT_TYPE,
  DEFAULT_BG_TO_COLOR,
  DEFAULT_FONT_TITLE,
  DEFAULT_FONT_TXT,
  DEFAULT_TXT_COLOR,
} from '~/utils/theme.constants';
import {produce} from 'immer';
import {isDark} from '@snapper/core';
import {getCourseData, selectIsCourseDataEmpty} from './course.selectors';

const DEBUG_FONT_TITLE = 'serif';
const DEBUG_FONT_TEXT = 'cursive';
const DEBUG_TEXT_COLOR = '#00ff00';
const DEBUG_BG_COLOR = '#ff00ff';
const DEBUG_BG_TO_COLOR = '#00ffff';
const DEBUG_BG_INPUT_TYPE = 'gradient';
const DEBUG_TEXT_BRIGHTNESS = isDark(DEBUG_TEXT_COLOR)
  ? 'dark'
  : 'light';

export const DEBUG_THEME = {
  color: DEBUG_BG_COLOR,
  colorTo: DEBUG_BG_TO_COLOR,
  bgInputType: DEBUG_BG_INPUT_TYPE,
  defaultFont: DEBUG_FONT_TEXT,
  defaultFontTitle: DEBUG_FONT_TITLE,
  txtColor: DEBUG_TEXT_COLOR,
  txtBrightness: DEBUG_TEXT_BRIGHTNESS,
};

export const DEFAULT_FONT_WEIGHTS = [
  '400',
  '400i',
  '500',
  '500i',
  '600',
  '600i',
  '700',
  '700i',
  '800',
  '800i',
  '900',
  '900i',
];

export const DEFAULT_FONTS = [
  {
    font: 'Abril Fatface',
    type: 'display',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Aclonica',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Alegreya',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Alegreya Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Archivo Narrow',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Arial',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Arvo',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Bangers',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'BioRhyme',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Caesar Dressing',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Cardo',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Caveat',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Chivo',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Cormorant Garamond',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Cormorant Upright',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Creepster',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Crimson Text',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'DM Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'DM Serif Display',
    type: 'display',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Dosis',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Eczar',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Euphoria Script',
    type: 'cursive',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Finger Paint',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Fira Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Fraunces',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Georgia',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Heebo',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'IBM Plex Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Inconsolata',
    type: 'monospace',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Indie Flower',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Inknut Antiqua',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Inter',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Jura',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Karla',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'KoHo',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Lato',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Libre Baskerville',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Libre Franklin',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Lobster',
    type: 'cursive',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Lora',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Manrope',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Merriweather',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Monoton',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Montserrat',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Neuton',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Noto Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Nunito',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Open Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Oswald',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Palatino',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Parisienne',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Playfair Display',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Poppins',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Prompt',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Proza Libre',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'PT Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'PT Sans Caption',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'PT Serif',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Raleway',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Righteous',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Roboto',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Rouge Script',
    type: 'cursive',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Rubik',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Rye',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Source Sans 3',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Source Serif 4',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Space Grotesk',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Space Mono',
    type: 'monospace',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Spectral',
    type: 'serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Style Script',
    type: 'cursive',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Syne',
    type: 'display',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Ubuntu',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Verdana',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
  {
    font: 'Work Sans',
    type: 'sans-serif',
    weights: DEFAULT_FONT_WEIGHTS,
  },
];

const NO_THEMES = {
  defaultTheme: null,
  themes: EmptyArray,
};

export const NOOP = () => {};

const DEFAULT_FONT_OPTIONS = DEFAULT_FONTS.map(({font, weights}) => ({
  label: font,
  value: font,
}));

export const selectFontOptions = createSelector(
  getConfigObject,
  config => {
    const fonts = [...DEFAULT_FONT_OPTIONS];
    const unique = new Set(fonts.map(f => f.value));

    (config?.getProperty('fonts') || []).forEach(f => {
      const font = f?.font || f?.value || f?.label;

      if (!font || unique.has(font)) return;

      unique.add(font);

      fonts.push({
        label: font,
        value: font,
        // skipLoad: !url,
        fontUrl: f?.fontUrl,
      });
    });

    return [...fonts]
      .sort((a, b) => a.label.localeCompare(b.label));
  },
);

export const selectFontUrlByFontName = createSelector(
  selectFontOptions,
  fonts => fonts?.reduce?.((acc, {label, fontUrl}) => {
    if (fontUrl) acc[label] = fontUrl;

    return acc;
  }, {}) || {},
);

export const selectTextElementFieldFontOptions = createSelector(
  selectFontOptions,
  fonts => fonts?.map?.(({label, value}) => ({
    text: label,
    value: {name: label, fontFamily: value},
  })) || EmptyArray,
);

export const selectConfigThemes = createSelector(
  getConfigObject,
  config => {
    if (!config?.data?.themes?.length) return NO_THEMES;

    let defaultTheme;
    const themes = [];

    for (const theme of config.data.themes) {
      if (theme?.color === DEFAULT_BG_COLOR) {
        defaultTheme = theme;
      } else {
        themes.push(theme);
      }
    }

    return {defaultTheme, themes};
  },
);

const STANDARD_TXT_COLORS = new Set(['#fff', '#ffffff', '#000', '#000000']);
const isStandardTxtColor = color => !color || STANDARD_TXT_COLORS.has(color.toLowerCase());

/** Mutate the slide item object to change the theme */
export const updateSlideItemTheme = (item, theme, pageTemplateType) => {
  if (!theme || !item) return item;

  const {data, type} = item || {};

  if (type === 'textElement') {
    data.color = isStandardTxtColor(theme.txtColor) ? null : theme.txtColor;

    const fontFamily = data?.size?.includes('xl')
      ? theme.defaultFontTitle || theme.defaultFont
      : theme.defaultFont || theme.defaultFontTitle;

    data.font = {
      fontFamily,
      name: fontFamily,
    };
  }

  return item;
};

/** Immutable version of updateSlideItemTheme */
export const updateSlideItemThemeImmutable = produce(updateSlideItemTheme);

/** Mutate the slide object to change the theme of the slide */
export const updateSlideTheme = (slide, theme, pageTemplateType) => {
  // console.log('updating slide theme...', {slide: original(slide), theme, pageTemplateType});

  if (!theme || !slide) return slide;

  slide.bgColor = theme.color;
  slide.bgToColor = theme.colorTo;
  slide.bgInputType = theme.bgInputType;

  const {items} = slide || {};

  if (!items?.length) return slide;

  for (const item of items) {
    updateSlideItemTheme(item, theme, pageTemplateType);
  }

  return slide;
};

/** Immutable version of updateSlideTheme */
export const updateSlideThemeImmutable = produce(updateSlideTheme);

/** Mutate the page object to change the theme of the page */
export const updatePageTheme = (page, theme = DEBUG_THEME) => {
  if (!theme) return page;

  const {templateTypeId} = page || {};

  if (!templateTypeId) return page;

  const {
    color,
    colorTo,
    bgInputType,
    defaultFont,
    defaultFontTitle,
    txtColor,
    txtBrightness,
  } = theme || {};

  const defaultFontTxt = defaultFont || defaultFontTitle;

  const pageThemeValues = {
    defaultFont: defaultFontTxt,
    defaultFontTitle: defaultFontTitle === defaultFontTxt ? undefined : defaultFontTitle,
    txtColor,
    bgColor: color,
    bgToColor: colorTo,
    bgInputType,
  };

  page.defaultTheme = {defaultFont: defaultFontTxt};
  // todo: get correct text colors and font size ...

  Object.assign(page, pageThemeValues);

  switch (templateTypeId) {
  case TEMPLATE_PICTURESLIDES: {
    const {pictureslides: {slides} = {}} = page || {};

    if (!slides?.length) return page;

    for (const slide of slides) {
      updateSlideTheme(slide, theme, templateTypeId);
    }

    break;
  }
  case TEMPLATE_DILEMMA: {
    const dilemmaColor = isStandardTxtColor(txtColor) ? null : txtColor;

    if (page?.dilemma?.question) {
      page.dilemma.question.color = dilemmaColor;
    }
    if (page?.dilemma?.answer) {
      page.dilemma.answer.color = dilemmaColor;
    }
  }
  }

  return page;
};

/** Immutable version of updatePageTheme */
export const updatePageThemeImmutable = produce(updatePageTheme);

/* Mutate the course object to change the theme of all pages */
export const updateCourseTheme = (course, theme) => {
  const {pages} = course || {};

  if (!theme || !pages?.length) return;

  for (const page of pages) {
    updatePageTheme(page, theme);
  }
};

/** Immutable version of updateCourseTheme */
export const updateCourseThemeImmutable = produce(updateCourseTheme);

const fallbackTheme = {
  color: DEFAULT_BG_COLOR,
  colorTo: DEFAULT_BG_TO_COLOR,
  bgInputType: DEFAULT_BG_INPUT_TYPE,
  defaultFont: DEFAULT_FONT_TXT,
  defaultFontTitle: DEFAULT_FONT_TITLE, // same as fontTxt if undefined
  txtColor: DEFAULT_TXT_COLOR,
};

const _selectGlobalConfigTheme = createSelector(
  getConfigObject,
  configObject => {
    if (!configObject?.data) return null;

    const {
      data: {
        themes,
        fonts,
      } = {},
    } = configObject || {};

    const configTheme = themes?.find?.(theme => theme?.['default']);
    const configFont = configTheme?.defaultFont || fonts?.find?.(font => font?.['default'])?.value;

    if (!configTheme?.color) return null;

    return {
      color: configTheme.color,
      colorTo: configTheme.colorTo,
      defaultFont: configFont,
      defaultFontTitle: configTheme.defaultFontTitle,
      txtColor: configTheme.txtColor,
    };
  },
);

const _selectCourseSettingsTheme = ({course}) => course?.data?.settings?.theme;

export const _selectGlobalCourseSettingsTheme = createSelector(
  selectIsCourseDataEmpty,
  _selectCourseSettingsTheme,
  (empty, courseSettingsTheme) => {
    if (empty) return null;

    const {
      theme, // always null?
      color,
      colorTo,
      txtColor,
      defaultFont,
      defaultFontTitle,
    } = courseSettingsTheme || {};

    if (!color) return undefined;

    return {
      color,
      colorTo,
      defaultFont,
      defaultFontTitle,
      txtColor,
    };
  },
);

export const selectActiveTheme = createSelector(
  _selectGlobalCourseSettingsTheme,
  _selectGlobalConfigTheme,
  (courseSettingsTheme, configTheme) => {
    if (courseSettingsTheme === null) return null;

    const themeFromParams = getThemeFromURLParams();

    const paramsTheme = themeFromParams?.color
      ? themeFromParams
      : null;

    // const isDefaultBgSetting = !courseSettingsTheme?.bgColor
    //   || (courseSettingsTheme.bgColor === configTheme?.bgColor || courseSettingsTheme.bgColor === fallbackTheme?.bgColor);
    // const isDefaultFontSetting = !courseSettingsTheme?.defaultFont
    //   || (courseSettingsTheme.defaultFont === configTheme?.defaultFont || courseSettingsTheme.defaultFont === fallbackTheme?.defaultFont);

    const activeTheme = courseSettingsTheme?.color // isDefaultBgSetting
      ? courseSettingsTheme || paramsTheme || configTheme || fallbackTheme
      : paramsTheme || courseSettingsTheme || configTheme || fallbackTheme;

    const {
      color = DEFAULT_BG_COLOR,
      colorTo,
      defaultFont = DEFAULT_FONT_TXT,
      defaultFontTitle,
      txtColor,
    } = activeTheme || {};

    const isBrightTxt = txtColor
      ? !isDark(txtColor)
      : colorTo
        ? isDark(colorTo) || isDark(color)
        : isDark(color);

    const defaultFontTxt = defaultFont || defaultFontTitle;

    return {
      color,
      colorTo,
      bgInputType: colorTo ? 'gradient' : 'solid',
      defaultFont: defaultFontTxt,
      defaultFontTitle: defaultFontTitle === defaultFontTxt
        ? undefined
        : defaultFontTitle,
      txtColor: txtColor ?? (isBrightTxt ? '#fff' : '#000'),
      txtBrightness: isBrightTxt ? 'light' : 'dark',
    };
  },
);

export const __DEBUG__getThemedCourseData = createSelector(
  getCourseData,
  selectActiveTheme,
  (courseData, activeTheme) => {
    if (!courseData) return null;

    // const themedCourse = updateCourseThemeImmutable(courseData, DEBUG_THEME || activeTheme);

    // console.log({themedCourse});

    return courseData; // themedCourse;
  },
);

export const getThemeDefaultFonts = () => {
  const {
    defaultFont,
    defaultFontTitle,
  } = getThemeFromURLParams();

  const font = defaultFont || defaultFontTitle;

  if (!font) return null;

  return {
    defaultFont: font,
    defaultFontTitle,
  };
};

export const findAllValuesByKey = (obj, key) => {
  const values = [];

  try {
    for (const [k, v] of Object.entries(obj)) {
      if (!v) continue;

      if (k === key) {
        values.push(v);
      } else if (typeof v === 'object') {
        values.push(...findAllValuesByKey(v, key));
      }
    }
  } catch (error) {
    console.error(error);
  }

  return values;
};

const selectAllCourseFonts = createSelector(getCourseData, courseData => {
  if (!courseData) return EmptyArray;

  const fonts = findAllValuesByKey(courseData, 'fontFamily');
  const defaultFonts = findAllValuesByKey(courseData, 'defaultFont');
  const themeFonts = getThemeDefaultFonts();

  return new Set([
    ...fonts,
    ...defaultFonts,
    themeFonts?.defaultFont,
    themeFonts?.defaultFontTitle,
  ]);
});

export const selectLazyPlayerFonts = createSelector(
  selectAllCourseFonts,
  selectFontUrlByFontName,
  (courseFonts, fontUrls) => {
    if (!courseFonts?.size) return EmptyArray;

    return [...courseFonts].map(fontName => typeof fontName === 'string'
      ? {
        font: fontName,
        weights: DEFAULT_FONT_WEIGHTS,
        fontUrl: fontUrls[fontName],
      }
      : null).filter(f => !!f);
  },
);

export const selectLazyEditorFonts = createSelector(
  selectLazyPlayerFonts,
  selectFontOptions,
  (playerFonts, fontOptions) => {
    const loadedFonts = new Set(playerFonts?.map(f => f?.font) || []);
    const fonts = [];

    fontOptions?.forEach(f => {
      const font = f?.value || f?.label;

      if (!font || loadedFonts.has(font)) return;

      fonts.push({
        font,
        weights: DEFAULT_FONT_WEIGHTS,
        fontUrl: f?.fontUrl,
      });
    });

    return fonts;
  },
);

export const selectAllThemes = createSelector(
  selectConfigThemes,
  configThemes => {
    const themeFromParams = getThemeFromURLParams();

    if (!themeFromParams?.color) return configThemes;

    const {defaultTheme, themes} = configThemes || NO_THEMES;

    const {color, colorTo, ...rest} = themeFromParams || {};

    return {
      defaultTheme: color
        ? {color, colorTo, ...rest}
        : defaultTheme,
      themes: color ? [defaultTheme, ...themes] : themes,
    };
  },
);
